import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type AbilitiesService from 'ember-can/services/abilities';
import type Store from 'ember-data/store';
import type Flow from 'garaje/models/flow';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import type StateService from 'garaje/services/state';
import { SignInFieldKind } from 'garaje/utils/enums';
import { TrackedSet } from 'tracked-built-ins';

import type { VisitorsQuestionnairesRouteModel } from '../route';

export interface VisitorsQuestionnairesCreateRouteModel {
  fetchFlowsPage: VisitorsQuestionnairesRouteModel['fetchFlowsPage'];
  changes: Set<Model>;
  flows: Flow[];
  location: Location;
  questionnaire: Questionnaire;
}

export default class VisitorsQuestionnairesRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;

  redirect(): void {
    if (this.abilities.cannot('create questionnaires')) {
      void this.router.replaceWith('visitors.questionnaires');
    }
  }

  model(): VisitorsQuestionnairesCreateRouteModel {
    const { fetchFlowsPage, flows, location } = <VisitorsQuestionnairesRouteModel>(
      this.modelFor('visitors.questionnaires')
    );

    const { changes, questionnaire } = this.#createEmptyQuestionnaire();

    return { changes, fetchFlowsPage, flows, location, questionnaire };
  }

  #createEmptyQuestionnaire(): { changes: Set<Model>; questionnaire: Questionnaire } {
    const changes = new TrackedSet<Model>();

    // Initialize a new questionnaire with a single (empty) section
    const questionnaire = this.store.createRecord('questionnaire', {
      title: 'New assessment',
      company: this.state.currentCompany,
      location: this.state.currentLocation,
    });
    changes.add(questionnaire);

    const section = this.store.createRecord('questionnaire-section', {
      position: 1,
      questionnaire,
    });
    changes.add(section);

    const question = this.store.createRecord('questionnaire-field', {
      questionnaireSection: section,
      kind: SignInFieldKind.SINGLE_SELECTION,
      position: 1,
    });
    changes.add(question);

    changes.add(
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 1 }),
    );
    changes.add(
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 2 }),
    );

    return { changes, questionnaire };
  }
}
